<template>
  <header :class="['container flex-v-center header', { 'header--mobile': isMobileHeader }]">
    <div class="grid">
      <div class="span12 flex-v-center">
        <a
          href="#hjem"
          title="Gå til top"
          data-gtm-action="header navigation"
          data-gtm-label="hjem"
          @click.prevent="scrollTo('hjem')"
        >
          <inline-svg class="logo" :src="prefixAssetUrl(norligLogo)" width="150" height="30" aria-label="Norli logo"
        /></a>

        <nav class="d-flex flex-h-right nav">
          <ul :class="['d-flex flex-v-center menu', { 'menu--mobile': isMobileHeader, active: isMenuActive }]">
            <li v-for="(link, index) in nav" :key="index">
              <a
                :class="[
                  'p-2',
                  {
                    active: scrollPosition >= link.positionTop && scrollPosition < link.positionBottom,
                  },
                ]"
                :href="`#${link.href}`"
                :title="link.title"
                data-gtm-action="header navigation"
                :data-gtm-label="link.title"
                @click.prevent="scrollTo(link.href)"
                v-text="link.title"
              />
            </li>
          </ul>
          <a
            class="button button--cta ml-4"
            href="/mit-norli"
            data-gtm-action="header navigation"
            data-gtm-label="log på"
            >Log på</a
          >
        </nav>

        <div v-if="isMobileHeader" class="flex-v-center ml-4" @click="store.commit('toggleMenu', !isMenuActive)">
          <hamburger :is-menu-active="isMenuActive" gtm-action-cat="header navigation" />
        </div>

        <teleport to="#app">
          <transition name="fade">
            <div v-if="(isMobileHeader && isMenuActive) || (isModalActive)" class="overlay" @click="handleOverlayClick()" />
          </transition>
        </teleport>
      </div>
    </div>
  </header>

  <main id="hjem" :class="['main', { 'main--mobile': isMobileHeader }]">
    <MaintenanceBanner v-if="showMaintenanceBanner" :text="maintenanceText" :classes="maintenanceColor" />
    <div class="hero container py-6 bg-tertiary">
      <div class="grid">
        <div
          v-inview="{ animate: true, animation: 'fade', offset: offset }"
          class="span8 p-4 d-flex flex-col hero__left bg-secondary"
          :style="`background: url(${prefixAssetUrl(heroBg)}) 30% 60% no-repeat; background-size: cover`"
        >
          <h1 class="hero__title mb-0" v-text="hero.left.title" />
          <!-- <div class="hero__splash bg-primary flex-v-center flex-col">
            <h3 class="m-0" v-text="hero.splash.title" />
            <span v-text="hero.splash.text" />
          </div>  -->
          <div class="hero__text p-3 mt-7 bg-white">
            <div v-html="hero.left.text" />
          </div>
        </div>
        <div
          v-inview="{ animate: true, animation: 'fade', offset: offset }"
          class="span4 p-4 d-flex flex-col hero__right bg-secondary"
        >
          <h2 class="mb-0" v-text="hero.right.title" />
          <h3 class="mt-0" v-text="hero.right.subtitle" />
          <div class="mt-2" v-html="hero.right.text" />
          <a
            data-gtm-action="hero"
            :data-gtm-label="hero.right.cta.title"
            class="button button--cta"
            :href="hero.right.cta.href"
            style="margin-top: auto"
            v-text="hero.right.cta.title"
          />
        </div>
        <!-- <div
          v-inview="{ animate: true, animation: 'fade', offset: offset }"
          class="span12 pt-6"
          >
          <inline-svg class="banner__desktop" :src="prefixAssetUrl(rynkebyDesktop)" width="1920" height="200" aria-label="Rynkeby banner"/>
          <inline-svg class="banner__mobile" :src="prefixAssetUrl(rynkebyMobile)" width="360" height="190" aria-label="Rynkeby banner mobile"/>
        </div> -->
      </div>
    </div>

    <div id="loesninger" class="solutions">
      <div class="container py-6">
        <div class="grid">
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span8 move2 mb-5">
            <h2 class="text-center">Pension med garanti</h2>
            <p style="font-size: 21px; line-height: 1.6; margin-top: 26px">
              Det kan være rart at vide, præcis hvor mange penge man som minimum vil få udbetalt hver måned, når man en dag går på pension – især hvis pensionen ikke ligger så langt ude i fremtiden. Det sørger vi for, at du som kunde ved.
              <br/><br/>
              I Norli hjælper vi nemlig med at sikre din pension gennem traditionelle, garanterede produkter. Det betyder, at vi garanterer dig en minimumsudbetaling ved pensionen, så du på forhånd ved, hvordan din økonomi i pensionstilværelsen kommer til at se ud.
            </p>
          </div>
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span6 mb-4 p-4 bg-primary">
            <h3>Traditionel pensionsopsparing <br/>(pension med garanti)</h3>
            <p>
              Du får en garanteret minimumsudbetaling – uanset hvordan finansmarkederne har set ud. Derudover kan du forvente at få mere – du får nemlig en årlig depotrente.  <br/><br/>
              I 2024 er depotrenten 2,5% for nye kunder. Vi er som alle andre afhængige af de finansielle markeder og kan ændre depotrenten, hvis det er nødvendigt. Dette vil dog ikke ændre ved din garanterede minimumsudbetaling.
            </p>
            <p>
              <a
                href="#faq"
                data-gtm-action="scroll link"
                data-gtm-label="Læs mere om traditionel opsparing"
                @click.prevent="scrollTo('faq')"
                >Læs mere om traditionel opsparing</a
              >
            </p>
          </div>
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span6 mb-4 p-4 bg-tertiary">
            <h3>Markedsrente</h3>
            <p>
              Med markedsrente følger afkastet på din pensionsopsparing udviklingen på de finansielle markeder. Din
              opsparing investeres i en puljeordning i Sydbank med udgangspunkt i din
              investeringsprofil.
            </p>
            <p>
              <a
                href="https://www.sydbank.dk/privat/opsparing/investering-af-din-opsparing"
                data-gtm-action="scroll link"
                data-gtm-label="Læs mere om markedsrente"
                target="_blank"
                >Læs mere om markedsrente</a
              >
            </p>
          </div>

          <div class="span8 move2 span10--md move1--md">
            <h3 v-inview="{ animate: true, animation: 'fade', offset: offset }">Opsparingstyper</h3>
            <p v-inview="{ animate: true, animation: 'fade', offset: offset }">
              Du kan placere din pensionsopsparing, så dine udbetalinger bliver skræddersyet til din pensionstilværelse.
              Der er 3 typer opsparing, som du kan kombinere, så de passer til dig.
            </p>
            <tabs v-inview="{ animate: true, animation: 'fade', offset: offset }" :data="tabsData" />
          </div>

          <div class="span8 move2 span10--md move1--md mt-5">
            <h3 v-inview="{ animate: true, animation: 'fade', offset: offset }">Pension med garanti</h3>
            <video id="bodyVideo" class="body-video" controls v-html="videoSrcHtml" :poster="videoPosterUrl" @click="toggleVideo()">
            </video>
          </div>

          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span12 my-5">
            <h2 class="text-center">Vores forsikringsløsninger</h2>
          </div>

          <div
            v-for="(product, index) in products"
            :key="'product-' + index"
            v-inview="{ animate: true, animation: 'fade', offset: offset }"
            class="span6 d-flex card mb-4"
          >
            <div class="card__icon mt-2 mr-3">
              <inline-svg :src="prefixAssetUrl(product.icon)" width="50" height="50" />
            </div>

            <div class="card__text pr-5">
              <h3 v-text="product.title" />
              <p v-text="product.text" />
              <p>
                <a
                  :href="product.link"
                  data-gtm-action="link"
                  :data-gtm-label="`Læs om forsikringen (${product.title})`"
                  target="_blank"
                  >Læs om forsikringen</a
                >
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="container pb-7">
        <div class="grid">
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span8 move2 span10--md move1--md">
            <h2 class="text-center mb-5">Pensionsordninger til firmaer og selvstændige</h2>
            <p>
              Tegn en firmapensionsordning med Norli, og gør din virksomhed til en attraktiv arbejdsplads for dine
              medarbejdere. En pensionsordning hos Norli giver dig og dine medarbejdere økonomisk tryghed hele livet og
              økonomisk sikkerhed ved sygdom og ulykker.
            </p>

            <p>
              Norli tilbyder som det eneste kommercielle pensionsselskab traditionel pensionsopsparing kombineret med opsparing
              i markedsrente. Det betyder, at dine medarbejdere får de mest fleksible muligheder for deres
              pensionsopsparing.
            </p>

            <p>
              Dine ældre medarbejdere kan således sikre sig størst mulig sikkerhed og garanti for deres pension ved at
              vælge en traditionel pensionsopsparing, imens dine yngre medarbejdere kan vælge en løsning med højere
              risiko og et større forventet afkast. Løsningerne kan også kombineres, så de passer præcist til den
              enkeltes behov.
            </p>

            <p>
              <a
                href="#kontakt"
                data-gtm-action="scroll link"
                data-gtm-label="Kontakt os"
                @click.prevent="scrollTo('kontakt')"
                >Kontakt os</a
              >
              for at høre mere om vores pensionsordninger til selvstændige og virksomheder.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div id="faq" class="bg-secondary">
      <div class="container py-7">
        <div class="grid">
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span12 mb-5">
            <h2 class="text-center">Ofte stillede spørgsmål</h2>
          </div>
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span8 move2 span10--md move1--md">
            <accordion :data="accordionData" />
          </div>

          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span12 mt-7 bg-white">
            <div class="grid">
              <div class="span5 flex-v-center p-5">
                <inline-svg :src="prefixAssetUrl(diabetesLogo)" />
              </div>
              <div class="span7 p-5 bg-primary">
                <h3>Tilbud til medlemmer af Diabetesforeningen</h3>
                <p>
                  Vi samarbejder med Diabetesforeningen om at bidrage til et sundere Danmark. 
                  Er du mellem 18 og 67 år og medlem af Diabetesforeningen, kan du få en fordelagtig forsikring, 
                  hvor du kan sikre, at dine efterladte får minimum 50.000 kr. udbetalt, hvis du dør, før du fylder 67 år.
                </p>
                <p>
                  <a
                    href="https://diabetes.dk/stot-os/bliv-medlem/medlemsfordele/norli-livsforsikring"
                    class="button mr-3 mb-1"
                    data-gtm-action="link"
                    data-gtm-label="Læs mere"
                    target="_blank"
                    >Se hvordan</a
                  >
                  <!-- <a
                    href="#kontakt"
                    class="button"
                    data-gtm-action="scroll link"
                    data-gtm-label="Kontakt os"
                    @click.prevent="scrollTo('kontakt')"
                    >Kontakt os</a
                  > -->
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="priser">
      <div class="container pt-7 pb-2">
        <div class="grid">
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span12 text-center mb-5">
            <h2>Priser, vilkår og blanketter</h2>
          </div>
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span4">
            <h4>Sundhedsforsikring</h4>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/sundhedsforsikring/sundhedsforsikring_ipid_januar-2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Produktinformation (IPID)"
                target="_blank"
                >Produktinformation (IPID)</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/sundhedsforsikring/sundhedsforsikring_privat_februar2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Vilkår og betingelser (privat)"
                target="_blank"
                >Vilkår og betingelser (privat)</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/sundhedsforsikring/sundhedsforsikring_erhverv_februar2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Vilkår og betingelser (erhverv)"
                target="_blank"
                >Vilkår og betingelser (erhverv)</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/sundhedsforsikring/hejdoktor_vejledning.pdf"
                data-gtm-action="link"
                data-gtm-label="Hejdoktor - vejledning"
                target="_blank"
                >Hejdoktor - vejledning</a
              >
            </p>


            <h4 class="mt-4">Livsforsikring</h4>

            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald_produktionformation-ipid_maj-2022.pdf"
                data-gtm-action="link"
                data-gtm-label="Produktinformation (IPID) - plusLIV ved dødsfald"
                target="_blank"
                >Produktinformation (IPID)<br />
                - plusLIV ved dødsfald</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/5277_plusliv_dod.pdf"
                data-gtm-action="link"
                data-gtm-label="Betingelser - PlusLIV ved dødsfald"
                target="_blank"
                >Betingelser - PlusLIV ved dødsfald</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald-og-kritisk-sygdom_produktinformation-ipid_maj-2022.pdf"
                data-gtm-action="link"
                data-gtm-label="Produktinformation (IPID) - plusLIV ved dødsfald og kritisk sygdom"
                target="_blank"
                >Produktinformation (IPID)<br />
                - plusLIV ved dødsfald og kritisk sygdom
              </a>
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/5276_plusliv.pdf"
                data-gtm-action="link"
                data-gtm-label="Betingelser - PlusLIV"
                target="_blank"
                >Betingelser - PlusLIV</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald-diabetesforeninge-ipid_maj-2022.pdf"
                data-gtm-action="link"
                data-gtm-label="Produktinformation (IPID) - plusLIV ved dødsfald (Diabetesforeningen)"
                target="_blank"
                >Produktinformation (IPID)<br />
                - plusLIV ved dødsfald (Diabetesforeningen)</a
              >
            </p>

            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald-diabetesforening-betingelser_juni-2022.pdf"
                data-gtm-action="link"
                data-gtm-label="Betingelser - plusLIV ved dødsfald (Diabetesforeningen)"
                target="_blank"
                >Betingelser<br />- plusLIV ved dødsfald (Diabetesforeningen)</a
              >
            </p>
            <!-- TODO: link to updated PDF -->
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/gruppeliv-betingelser---dod-og-invaliditet.pdf"
                data-gtm-action="link"
                data-gtm-label="Gruppeliv - død og invaliditet"
                target="_blank"
                >Gruppeliv - død og invaliditet</a
              >
            </p>
            <!-- TODO: link to updated PDF -->
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/gruppeliv-betingelser---kritisk-sygdom.pdf"
                data-gtm-action="link"
                data-gtm-label="Gruppeliv - kritisk sygdom"
                target="_blank"
                >Gruppeliv - kritisk sygdom</a
              >
            </p>
            <!-- TODO: link to updated PDF -->
            <p>
              <a
                href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/gruppeliv---kritisk-sygdom-born.pdf"
                data-gtm-action="link"
                data-gtm-label="Gruppeliv - kritisk sygdom (børn)"
                target="_blank"
                >Gruppeliv - kritisk sygdom (børn)</a
              >
            </p>
          </div>
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span4">
            <h4>Traditionel pensionsopsparing</h4>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/traditionel-pensionsopsparing/betingelser-for-livs--og-pensionsforsikring_vs2.pdf"
                data-gtm-action="link"
                data-gtm-label="Betingelser (Livs- og pensionsforsikring)"
                target="_blank"
                >Betingelser (Livs- og pensionsforsikring)</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/traditionel-pensionsopsparing/betingelser-for-kontopension.pdf"
                data-gtm-action="link"
                data-gtm-label="Betingelser (Kontopension)"
                target="_blank"
                >Betingelser (Kontopension)</a
              >
            </p>

            <h4 class="mt-4">Forsikring ved nedsat erhvervsevne</h4>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/erhvervsevne/nedsat_erhvervsevne_ipid_marts2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Produktinformation (IPID) - nedsat erhvervsevne"
                target="_blank"
                >Produktinformation (IPID)<br />- nedsat erhvervsevne</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/erhvervsevne/nedsat-erhvervsevne-betingelser.pdf"
                data-gtm-action="link"
                data-gtm-label="Betingelser for nedsat erhvervsevne"
                target="_blank"
                >Betingelser for nedsat erhvervsevne</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/erhvervsevne/nedsat_erhvervsevne_firmakoncept_ipid_marts2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Produktinformation (IPID) - Firmakoncept, nedsat erhvervsevne"
                target="_blank"
                >Produktinformation (IPID)<br />- Firmakoncept, nedsat erhvervsevne</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/erhvervsevne/nedsat-erhvervsevne-firmakoncept-betingelser.pdf"
                data-gtm-action="link"
                data-gtm-label="Betingelser for Firmakoncept, nedsat erhvervsevne"
                target="_blank"
                >Betingelser for Firmakoncept,<br />
                nedsat erhvervsevne</a
              >
            </p>
          </div>
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span4">
            <h4>Generelt</h4>
            <p>
              <link-collection :data="historicTerms" />
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/generelt/serviceydelser.pdf"
                data-gtm-action="link"
                data-gtm-label="Serviceydelser"
                target="_blank"
                >Serviceydelser</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/traditionel-pensionsopsparing/traditionel-pensionsopsparing---priser-og-gebyrer.pdf"
                data-gtm-action="link"
                data-gtm-label="Omkostninger for administration"
                target="_blank"
                >Omkostninger for administration</a
              >
            </p>
            <p>
              <button class="modal-trigger" type="button" @click="store.commit('toggleModal', !isModalActive)">Se filmen - Pension med garanti</button>
            </p>
            <p>
              <a
                href="/forenede-gruppeliv"
                data-gtm-action="link"
                data-gtm-label="forenede gruppeliv"
                target="_blank"
                >Gruppelivsforsikringer<br/> administreret af Forenede Gruppeliv</a
              >
            </p>

            <h4 class="mt-4">Blanketter</h4>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/blanketter/begunstigelseserklaring.pdf"
                data-gtm-action="link"
                data-gtm-label="Begunstigelseserklæring"
                target="_blank"
                >Begunstigelse</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/blanketter/bandlaggelse.pdf"
                data-gtm-action="link"
                data-gtm-label="Båndlæggelse"
                target="_blank"
                >Båndlæggelse</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/blanketter/sareje.pdf"
                data-gtm-action="link"
                data-gtm-label="Særeje"
                target="_blank"
                >Særeje</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/blanketter/bestilling-af-sundhedsforsikring_if_norli_privatkunde_2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Bestilling af sundshedsforsikring (privat)"
                target="_blank"
                >Bestilling af sundshedsforsikring (privat)</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/blanketter/bestilling-af-sundhedsforsikring_if_norli_virksomhedskunde_2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Bestilling af sundshedsforsikring (erhverv)"
                target="_blank"
                >Bestilling af sundshedsforsikring (erhverv)</a
              >
            </p>
         </div>

          <div
            v-inview="{ animate: true, animation: 'fade', offset: offset }"
            class="span12 mt-7"
            :style="`background: url(${prefixAssetUrl(pensionBg)}) left 30% no-repeat; background-size: 50%`"
          >
            <div class="grid">
              <div class="span5 flex-v-center"></div>
              <div class="span7 p-4 bg-primary">
                <h3>Regn på omkostningerne</h3>
                <p>
                  Her har du mulighed for at få et indtryk af de samlede omkostninger ved at have en pensionsordning hos
                  os – både i kroner og i procent.
                </p>
                <p>
                  <button
                    type="button"
                    class="button"
                    data-gtm-action="link"
                    data-gtm-label="Prøv omkostningsmåleren"
                    @click="toggleCalculator"
                  >
                    Prøv omkostningsmåleren
                  </button>
                </p>
              </div>
            </div>
          </div>

          <transition name="slide">
            <calculator id="omkostningsmaaler" v-show="isCalculatorOpen" />
          </transition>
        </div>
      </div>
    </div>

    <div id="om">
      <div class="container pt-5">
        <div class="grid">
          <div
            v-inview="{ animate: true, animation: 'fade', offset: offset }"
            class="span8 move2 span10--md move1--md mb-5"
          >
            <h2 class="text-center">Om Norli Liv og Pension</h2>
            <p>
              Norli Liv og Pension er et dansk pensionsselskab, der som det eneste selskab tilbyder nye kunder en
              traditionel pensionsopsparing som en del af deres pensionsordning.
            </p>
            <p>
              Vi blev grundlagt i 1986 som en del af Alm. Brand koncernen og hed tidligere Alm Brand Liv og Pension. I
              foråret 2022 fik vi nye ejere og skiftede navn til Norli Liv og Pension.
            </p>
            <p>
              I dag er vi en del af Norli Group, som fokuserer på pensionsforsikring i de nordiske lande og har en klar
              målsætning om at være markedsledende inden for traditionel pensionsopsparing.
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/om-norli/ekstern-udmelding.pdf"
                data-gtm-action="link"
                data-gtm-label="Ekstern udmelding"
                target="_blank"
                >Ekstern udmelding</a
              >
            </p>
          </div>

          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span3 span6--md">
            <h4 class="mt-4">Bestyrelse</h4>
            <p><strong>Vivian Lund</strong><br />Formand for bestyrelsen</p>
            <p><strong>Henrik Gade Jepsen</strong><br />Medlem af bestyrelsen</p>
            <p><strong>Nina Christensen</strong><br />Medlem af bestyrelsen</p>
          </div>

          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span3 span6--md">
            <h4 class="mt-4">Direktion</h4>
            <p><strong>Claude Reffs </strong><br />Adm. direktør</p>
            <p><strong>Peter Trägårdh Christensen </strong><br />Koncerndirektør, CRO</p>

            <h4 v-inview="{ animate: true, animation: 'fade', offset: offset }" class="mt-4">Ansvarshavende aktuar</h4>
            <p><strong>Anders Pilegaard Håkonsson</strong><br />Ansvarshavende aktuar</p>
          </div>

          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span3 span6--md">
            <h4 class="mt-4">Rapportering</h4>
            <p><link-collection :data="riskReports" /></p>
            <p><link-collection :data="annualReports" /></p>
            <p><link-collection :data="financeReports" /></p>
            <h4 v-inview="{ animate: true, animation: 'fade', offset: offset }" class="mt-4">Vederlagsrapporter</h4>
            <p><link-collection :data="remuneration" /></p>
          </div>
         
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span3 span6--md">
            <h4 class="mt-4">Politikker</h4>
            <p>
              <a
                href="/siteassets/site-images/pdf/politikker/ng-lonpolitik-20240402.pdf"
                data-gtm-action="link"
                data-gtm-label="Vederlagspolitik"
                target="_blank"
                >Vederlagspolitik</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/politikker/politik-for-ansvarlige-investeringer-og-aktivt-ejerskab---norli-gruppen-2023-20230328-ekskl.-rev.-log.pdf"
                data-gtm-action="link"
                data-gtm-label="Politik for ansvarlige investeringer"
                target="_blank"
                >Politik for ansvarlige investeringer</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/politikker/persondatapolitik-norli-liv-og-pension.pdf"
                data-gtm-action="link"
                data-gtm-label="Persondatapolitik"
                target="_blank"
                >Persondatapolitik</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/politikker/persondatapolitik-for-jobansogere-2024.pdf"
                data-gtm-action="link"
                data-gtm-label="Brug af persondata – jobansøgere"
                target="_blank"
                >Brug af persondata – jobansøgere</a
              >
            </p>
            <p>
              <a
                href="/siteassets/site-images/pdf/politikker/cookiepolitik-noli-liv-og-pension.pdf"
                data-gtm-action="link"
                data-gtm-label="Cookies"
                target="_blank"
                >Cookies</a
              >
            </p>
          </div>

          <div
            v-inview="{ animate: true, animation: 'fade', offset: offset }"
            class="span6 bg-tertiary p-4 d-flex flex-col my-5"
          >
            <h3>Job i Norli</h3>
            <p class="mb-5">
              I Norli investerer vi i vores medarbejderes trivsel og udvikling. Vi har en fleksibel arbejdsplads med
              kunden i centrum, hvor der er højt til loftet og der er plads til og mulighed for faglig udvikling.
            </p>
            <p style="margin-top: auto">
              <a
                href="https://www.jobindex.dk/jobsoegning?q=norli"
                target="_blank"
                data-gtm-label="Se ledige stillinger"
                data-gtm-action="link"
                class="button"
                >Se ledige stillinger</a
              >
            </p>
          </div>

          <div
            v-inview="{ animate: true, animation: 'fade', offset: offset }"
            class="span6 bg-tertiary p-4 d-flex flex-col my-5"
          >
            <h3>Hvis du ikke er tilfreds</h3>
            <p>
              Vi vægter kundeservice meget højt. Derfor vil vi gerne høre fra dig, hvis du er utilfreds med den service
              eller sagsbehandling, du har fået hos os.
            </p>
            <p class="mb-5">
              Hvis du ønsker at klage, har vi udarbejdet en
              <a
                href="/siteassets/site-images/pdf/klagevejledning/klagevejledning-norli-liv-og-pension.pdf"
                data-gtm-action="link"
                data-gtm-label="klagevejledning"
                target="_blank"
                >klagevejledning</a
              >. Er du uenig i vores svar på din klage, kan du klage til
              <a
                href="https://ankeforsikring.dk/Sider/default.aspx"
                data-gtm-action="link"
                data-gtm-label="Ankenævnet for Forsikring"
                target="_blank"
                >Ankenævnet for Forsikring</a
              >
              mod et mindre gebyr til dem.
            </p>
            <p style="margin-top: auto">
              <a
                href="#kontakt"
                class="button"
                data-gtm-action="scroll link"
                data-gtm-label="kontakt os"
                @click.prevent="scrollTo('kontakt')"
                >Kontakt os</a
              >
            </p>
          </div>
        </div>
      </div>
    </div>
    <div id="pensions">
      <div class="container">
        <div class="grid">
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span12">
            <div class="p-4 bg-secondary text-center">
              <h2 class="text-center">For pensionsbranchen</h2>
              <p>I Norli er vi 100% fokuserede på traditionelle garanterede pensionsprodukter, og vi ønsker at tilbyde andre pensionsselskaber at få glæde af vores fokus.</p>
              <button @click.self="togglePensionBranch" @keyup.enter="togglePensionBranch" class="pension-read-more text-large m-0" :class="{'active': showPensionBranch}">Læs mere</button>
            </div>
            <transition name="slide" @after-enter="updatePensionsHeight" @before-leave="updatePensionsHeight">
              <div v-if="showPensionBranch" class="p-4 bg-tertiary" id="pensionsAccordion">
                <p>Tanken bag Norli er at samle de traditionelle garanterede pensionsprodukter i ét setup.</p>
                <p>Det kan der naturligvis være direkte synergier i, men lige så vigtigt er det, at de øvrige pensionsselskaber slipper for at bruge fokus på produkter, som de ikke længere markedsfører. For kunder med traditionelle garantier betyder et samlet setup, at de forsat kan føle sig trygge et sted, hvor man specialiserer sig i lige præcis deres produkt.</p>
                <p>I Norli er vi 100% fokuserede på traditionelle garanterede pensionsprodukter, og vi kan også tilbyde andre pensionsselskaber at få glæde af vores fokus.</p>
                <p>
                  <strong>Fordele for pensionsselskaber</strong>
                  <br>
                  Der er mange fordele for andre pensionsselskaber ved at samle de traditionelle garanterede produkter ét sted – f.eks.:
                </p>
                <ul>
                  <li>At skifte den uforudsigelige indtjening fra gennemsnitsrentebestanden ud med en, der er mere forudsigelig</li>
                  <li>At kunne koncentrere det ledelses- og medarbejdermæssige fokus mod at drive og udvikle markedsrenteprodukter</li>
                  <li>At undgå den solvensbinding, der ligger i de traditionelle produkter</li>
                  <li>At slippe for at udvikle og vedligeholde IT-systemer til de traditionelle produkter.</li>
                </ul>
                <p>Der findes en række forskellige muligheder for, hvordan et selskab kan opnå de ønskede fordele. Det kan f.eks. være med direkte køb af porteføljer eller med andre former for hel eller delvis risikooverførsel, hvor der eventuelt også kan følge en tilhørende outsourcing af de administrative opgaver med.</p>
                <p>
                  <strong>Vil du vide mere?</strong>
                  <br>
                  Kontakt:
                  <br>
                  Søren Andersen, Group CEO<br>Tlf.: <a href="tel:30534001">30 53 40 01</a>
                </p>
              </div>
            </transition>
          </div>
          <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span8 move2 span10--md move1--md mb-5 pt-5 animate fade">
              <h2 class="text-center">Norli Group</h2>
              <p>I dag er vi en del af Norli Group, som fokuserer på pensionsforsikringer i de nordiske lande og har en klar målsætning om at være markedsledende inden for traditionel pensionsopsparing.</p>
            </div>
            <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span4 span6--md">
              <h4>Politikker</h4>
              <p><link-collection :data="politicalReports" /></p>
              <p>
                <a
                  href="/siteassets/site-images/pdf/politikker/ng-lonpolitik-20240402.pdf"
                  data-gtm-action="link"
                  data-gtm-label="Vederlagspolitik"
                  target="_blank"
                  >Vederlagspolitik</a
                >
              </p>
            </div>
            <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span4 span6--md">
            <h4>Vigtigt at vide</h4>
              
            </div>
            <div v-inview="{ animate: true, animation: 'fade', offset: offset }" class="span4 span6--md">
              <h4>Rapportering</h4>
              <p><link-collection :data="nordicFinanceReports" /></p>
              <p><link-collection :data="nordicRiskReports" /></p>
              <p><link-collection :data="nordicAnnualReports" /></p>
              <p><link-collection :data="remunerationAnnualReports" /></p>
              <p><link-collection :data="sustainabilityFactorsReports" /></p>
            </div>
        </div>
      </div>
    </div>
    <transition name="fade">
      <div v-if="isModalActive" class="video-modal">
        <button class="close-overlay" @click="store.commit('toggleModal', false)">X</button>
        <video controls v-html="videoSrcHtml">
        </video>
      </div>
    </transition>    
  </main>

  <footer-component />
</template>

<script setup lang="ts">
import { onMounted, ref, computed, inject } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import Hamburger from '@/components/Hamburger/Hamburger.vue'
import Accordion from '@/components/Accordion/Accordion.vue'
import MaintenanceBanner from '@/components/MaintenanceBanner/MaintenanceBanner.vue'
import pensionBg from '@/assets/img/banner-bg-calculator.jpg'
import heroBg from '@/assets/img/hero-bg-alt.jpg'
import norligLogo from '@/assets/svg/norli-logo.svg'
import diabetesLogo from '@/assets/svg/diabetes-logo.svg'
import rynkebyDesktop from '@/assets/svg/rynkeby-banner-desktop.svg'
import rynkebyMobile from '@/assets/svg/rynkeby-banner-mobile.svg'
import chevron from '@/assets/svg/chevron.svg'
import iconBriefcase from '@/assets/svg/daekning_nedsat_erhvervsevne_icon_norli.svg'
import iconMedical from '@/assets/svg/sundhedsforsikring_icon_norli.svg'
import iconHeart from '@/assets/svg/livsforsikring_icon_norli.svg'
import iconThermometer from '@/assets/svg/forsikring_ved_kritisk_sygdom_icon_norli.svg'
import LinkCollection from '@/components/LinkCollection/LinkCollection.vue'
import Tabs from '@/components/Tabs/Tabs.vue'
import FooterComponent from '@/components/Footer/Footer.vue'
import Calculator from '@/components/Calculator/Calculator.vue'

const store = useStore()
const route = useRoute()

const assetUrlPrefix = inject('assetUrlPrefix') as string
console.log('assetUrlPrefix', assetUrlPrefix)

const prefixAssetUrl = (url: string): string => {
  if (import.meta.env.DEV) return url

  const filename = url.slice(2)
  return `${assetUrlPrefix}/${filename}`
}

let scrollPosition = ref(0)

const videoSrcHtml = '<source src="/siteassets/site-images/video/norli-animation.mp4" type="video/mp4" />';
const videoPosterUrl = '/siteassets/site-images/video/video-poster.jpg';

const offset = '-100px'

const showMaintenanceBanner = window.showBanner;
const maintenanceText = window.bannerText;
const maintenanceColor = window.bannerColor;

const nav = ref([
  {
    title: 'Vores løsninger',
    href: 'loesninger',
    positionTop: 0,
    positionBottom: 0,
    active: false,
    offsetBottom: 0,
    offsetTop: 0
  },
  {
    title: 'Spørgsmål og svar',
    href: 'faq',
    positionTop: 0,
    positionBottom: 0,
    active: false,
    offsetBottom: 0,
    offsetTop: 0
  },
  {
    title: 'Om os',
    href: 'om',
    positionTop: 0,
    positionBottom: 0,
    active: false,
    offsetBottom: 0,
    offsetTop: 0
  },
  {
    title: 'For pensionsbranchen',
    href: 'pensions',
    positionTop: 0,
    positionBottom: 0,
    active: false,
    offsetBottom: 100,
    offsetTop: 0
  },
  {
    title: 'Kontakt',
    href: 'kontakt',
    positionTop: 0,
    positionBottom: 0,
    active: false,
    offsetBottom: 0,
    offsetTop: 100
  },
])

const hero = {
  splash: {
    title: "Nomineret",
    text: "til årets pensionsselskab 2022",
  },
  left: {
    title: 'Velkommen til Norli Liv og Pension',
    text: '<p>Hos Norli tilbyder vi attraktive pensionsordninger til private, selvstændige og erhverv. Du får adgang til markedets bredeste produktudbud, der består af både traditionel pensionsopsparing, opsparing i markedsrente, sundheds- og livsforsikringer samt dækning ved nedsat erhvervsevne.</p>',
  },
  right: {
    title: 'Dit overblik',
    subtitle: '- nemt og hurtigt',
    text: '<p>Log på Mit Norli og få det fulde overblik over dine pensionsopsparinger.</p>',
    cta: {
      title: 'Log på og se din ordning',
      href: '/mit-norli',
    },
  },
}

const products = [
  {
    title: 'Sundhedsforsikring',
    text: 'Sikrer dig hurtig adgang til behandling på privathospital, hvis du får en skade eller på anden måde skal undersøges eller i behandling.',
    link: `/siteassets/site-images/pdf/forsikringstyper/sundhedsforsikring/sundhedsforsikring_ipid_januar-2024.pdf`,
    icon: iconMedical,
  },
  {
    title: 'Livsforsikring',
    text: 'Sikrer dine efterladte økonomisk i tilfælde af, at du dør. Du bestemmer selv, hvem der skal have pengene udbetalt.',
    link: `/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald_produktionformation-ipid_maj-2022.pdf`,
    icon: iconHeart,
  },
  {
    title: 'Forsikring ved kritisk sygdom',
    text: 'Sikrer dig en engangsudbetaling, hvis du får en kritisk sygdom. Det kan give dig og din familie et økonomisk pusterum i en svær tid.',
    link: `/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald-og-kritisk-sygdom_produktinformation-ipid_maj-2022.pdf`,
    icon: iconThermometer,
  },
  {
    title: 'Forsikring ved nedsat erhvervsevne',
    text: 'Sikrer dig økonomisk, hvis du helt eller delvist mister evnen til at arbejde pga. sygdom eller ulykke.',
    link: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/erhvervsevne/nedsat_erhvervsevne_ipid_marts2024.pdf`,
    icon: iconBriefcase,
  },
]

const accordionData = [
  {
    title: 'Generelt',
    accordionItems: [
      {
        question: 'Hvor kan jeg få overblik over min pension?',
        answer:
          '<strong>Mit Norli</strong><p>Hvis du har en pensionsordning hos os, kan du få et overblik over dine ordninger ved at bruge Mit Norli (tidligere Mit Kundeoverblik).</p><p><a href="/mit-norli" target="_blank" data-gtm-action="link" data-gtm-label="Log på Mit Norli">Log på Mit Norli</a></p><p>Når du er logget på, kan du bl.a. se:</p><ul><li>Din garanterede pensionsudbetaling</li><li>Prognose for de forventede pensionsudbetalinger</li><li>Dine forsikringsdækninger</li><li>Udviklingen i opsparingen hver måned</li><li>Nøgletal</li><li>Skatteoplysninger</li><li>Din indbakke</li></ul><strong>Norli Sundhed</strong><p>Hvis du har spørgsmål til din sundhedsforsikring, kan du ringe på telefon 70 13 13 03.</p>',
      },
      {
        question: 'Hvad er forskellen på en pensionsopsparing og en pensionsordning?',
        answer: `<p>En pensionsopsparing er den opsparing, som du løbende indbetaler til, og som udbetales, når du går på pension. En pensionsordning dækker lidt bredere, da den både rummer din pensionsopsparing og en række valgfrie personforsikringer. De valgfrie personforsikringer har til formål at sikre dig, hvis du skulle blive udsat for ulykke, sygdom eller lignende, inden du går på pension. Vi tilbyder disse personforsikringer:</p><ul><li><a href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald_produktionformation-ipid_maj-2022.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Livsforsikring">Livsforsikring</a></li><li><a href="/siteassets/site-images/pdf/forsikringstyper/livsforsikring/plusliv-ved-dodsfald-og-kritisk-sygdom_produktinformation-ipid_maj-2022.pdf" target="_blank" data-gtm-action="link"
                    data-gtm-label="Forsikring ved kritisk sygdom">Forsikring ved kritisk sygdom</a></li><li><a href="/siteassets/site-images/pdf/forsikringstyper/sundhedsforsikring/sundhedsforsikring_produktinformation-ipid_maj-2022.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Sundhedsforsikring">Sundhedsforsikring</a></li><li><a href="/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/erhvervsevne/nedsat_erhvervsevne_ipid_marts2024.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Forsikring ved nedsat erhvervsevne">Forsikring ved nedsat erhvervsevne</a></li></ul><p>Typisk vil langt de fleste af de penge, du indbetaler på din pensionsordning gå til din pensionsopsparing, mens en mindre del af pengene går til personforsikringerne.</p>`,
      },
      {
        question: 'Hvad sker der med min pension, hvis jeg skal skilles?',
        answer:
          '<p>Når pensioner skal deles i forbindelse med skilsmisse, er hovedreglen, at pensionen følger personen, hvis pensionen er ”rimelig”. Det gælder alle pensionstyper. En pension er ”rimelig”, hvis den svarer til, hvad der er normalt for dit fag og din uddannelse, og arbejdsmarkedspensioner og overenskomstbaserede pensionsordninger anses for "rimelige".</p><p>Det er skifteretten, der vurderer, om en pension er ”ikke rimelig” og i det tilfælde skal pensionen deles mellem jer.</p><p>I kan vælge at oprette en ægtepagt, hvor I bestemmer, hvordan jeres pensioner skal deles i tilfælde af skilsmisse. En ægtepagt udarbejdes af en advokat, men hvis I ikke har behov for advokatrådgivning, kan I købe en formular online. Husk at en ægtepagt skal tinglyses for at være gyldig.</p>',
      },
      {
        question: 'Hvad skal jeg gøre, hvis jeg vil have min pensionsopsparing udbetalt før tid?',
        answer:
          '<p>I nogle situationer kan det være nødvendigt at få udbetalt sin pension før det aftalte tidspunkt. Eller måske har du valgt at flytte til et andet pensionsselskab. I disse situationer er der en række regler, du bør være opmærksom på, inden du træffer dit valg om tilbagekøb, som det også hedder.</p><p>Det koster et administrationsgebyr, hvis du tilbagekøber din pensionsopsparing.</p><p>Vær opmærksom på, at nogle ordninger ikke kan flyttes eller udbetales før det aftalte tidspunkt.</p><strong>Skat og afgift</strong><p>Inden du får udbetalt din pensionsopsparing trækker vi også de lovpligtige skatter og afgifter fra. Skatter og afgifter beregnes efter de til enhver tid gældende regler og satser.</p>',
      },
      {
        question: 'Hvad sker der med min pension, hvis jeg dør?',
        answer:
          '<p>Hvis du dør, inden du går på pension, bliver din pensionsopsparing udbetalt til de begunstigede - dvs. dem der skal arve dig. De skal betale en afgift til staten på 40 % af din pension. Hvis du også har en Livsforsikring, bliver den også udbetalt til de begunstigede ved din død.</p><p>Hvis du dør, efter du er gået på pension, og du stadig har noget af din pensionsopsparing tilbage, vil dine arvinger få udbetalt din resterende pensionsopsparing. Der gælder forskellige regler for de forskellige pensionstyper:</p><strong>Ratepension</strong><p>Det tilbageværende beløb udbetales, og der skal betales en afgift til staten.</p><strong>Alderspension</strong><p>Engangsbeløbet kan udbetales til arvinger, hvis det ikke er udbetalt endnu. Der skal ikke betales afgift til staten på udbetalingen.</p><strong>Livsvarig pension</strong><p>En forholdsmæssig ydelse af det resterende beløb kan udbetales, hvis det er tilvalgt, da pensionsopsparingen blev oprettet. Der skal betales indkomstskat ved udbetaling.</p>',
      },
      {
        question: 'Hvordan er skattereglerne?',
        answer: `<p>Alt efter hvilken pensionsopsparing du vælger, gælder der forskellige regler for fradrag og beskatning. Der er også forskel på, om din pensionsordning er en firmabetalt ordning, eller om du selv betaler. Er du i tvivl, er du velkommen til at kontakte os for nærmere rådgivning.</p><p><a href="/siteassets/site-images/pdf/faq/oversigt-med-skatteregler-norli.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se oversigt med skatteregler (pdf)">Se oversigt med skatteregler (pdf)</a></p><p>Besøg <a href="https://skat.dk/skat.aspx?oid=2234742" target="_blank" data-gtm-action="link" data-gtm-label="skat.dk">skat.dk</a> for mere info om skatteregler.</p>`,
      },
      {
        question: 'Hvad sker der, hvis jeg holder op med at indbetale til min pension?',
        answer:
          '<p>Hvis du stopper med at indbetale til din ordning, inden det aftalte tidspunkt, ændrer vi din ordning til en fripolice. En fripolice er med andre ord en indbetalingsfri ordning. Din pension fortsætter og pensionsudbetalingerne nedjusteres så de passer til, at du ikke længere indbetaler til ordningen.</p><p>Har du tilknyttet forsikringsdækninger til din pensionsordning, vil disse som udgangspunkt bortfalde hvis indbetalingen stopper.</p><p>Der kan være undtagelser, hvor der ikke er muligt at ændre til en fripolice. I så fald vil din ordning helt ophøre.</p><p>Du er altid velkommen til at kontakte os på telefon 38 20 24 20 eller på service@norli.dk, hvis du har spørgsmål.</p>',
      },
      {
        question: 'Hvordan indsender jeg PensionsInfo?',
        answer:
          '<p>Det er nemt at sende din PensionsInfo rapport til Norli – sådan gør du</p><ol><li>Log på Mit Norli og klik på "Log på Pensionsinfo"</li><li>Klik på "Fortsæt" når alle oplysninger er indsamlet</li><li>Klik på "Hent rapport"</li><li>Klik på "Send oplysninger"</li><li>Send rapporten ved til sidst at klikke på "Send oplysninger for mig" nederst på siden</li></ol><p>Norli vil så modtage oplysningerne fra PensionsInfo, og vi er dermed bedre i stand til at rådgive dig når vi taler sammen.</p>',
      },
      {
        question: 'Sparer du nok op til pension? ',
        answer:
          '<p>Brancheorganisationen Forsikring & Pension har lavet en pensionsmåler, hvor du kan få en idé om, hvor meget du kan forvente at få i pension.</p><p>Prøv <a href="https://www.pensionsmaaler.dk" target="_blank" data-gtm-action="link" data-gtm-label="pensionsmåleren">pensionsmåleren</a></p>',
      },
      {
        question: 'For firmakunder: Hvordan til-/framelder jeg medarbejdere i vores ordning?',
        answer:
          '<p>Har du fået en ny medarbejder, der skal tilmeldes jeres firmaordning, eller stopper en nuværende?<br/>Så behøver du blot at udfylde medarbejderens informationer i ét af de to nedenstående links herunder:</p><p><a href="https://landing.webcrm.com/Start.aspx?awb=iquuqakiaeceaeimqaissomagqc" target="_blank" data-gtm-action="link" data-gtm-label="nymedarbejder">Tilmeld ny medarbejder</a><br/><br/>Vi kontakter derefter din nye medarbejder og aftaler et tidspunkt for et pensionsmøde. <br/><br/><a href="https://landing.webcrm.com/Start.aspx?awb=iscccakiaeckaeimqaissogagoq" target="_blank" data-gtm-action="link" data-gtm-label="afmeldmedarbejder">Afmeld medarbejder</a><br/><br/>Vi sørger for, at medarbejderen bliver frameldt jeres ordning.</p>',
      },
    ],
  },
  {
    title: 'Detaljer',
    accordionItems: [
      {
        question: 'Hvordan fungerer en traditionel pensionsopsparing?',
        answer:
          '<p>En traditionelle pensionsordninger - eller, som de også kaldes en pensionsopsparing med gennemsnitsrente - er baseret på forsikringstankegangen, hvor du som kunde deltager i et fællesskab, dvs. en gruppe af kunder.</p><p>Har du en livsforsikring, der dækker ved dødsfald, eller en forsikring der dækker, hvis du mister erhvervsevnen, betaler du ind til fællesskabet. Fællesskabets kasse betaler så til dem, der bliver syge eller til de efterladte ved dødsfald.</p><p>Hvis du har en ordning, hvor du sparer op til pensionen, indgår du i et investeringsfællesskab. Alle indbetalingerne bliver investeret i værdipapirer og i fast ejendom. Afkastet går ind i investeringsfællesskabet, som betaler en rente (depotrenten) til dig som kunde.</p><p>For at kunne give en stabil rente tilstræber vi, at depotrenten ikke svinger for meget fra år til år. I år med højt afkast gemmer vi en del af afkastet til senere år, og i år med lavt afkast tager vi fra det opsparede afkast.</p><p>Depotrenten i det enkelte år afhænger af, hvilken kundegruppe du tilhører. Læs mere om kundegrupper i næste punkt.</p>',
      },
      {
        question: 'Hvad er kundegrupper?',
        answer:
          '<p><strong>Hvad er kundegrupper?</strong><br>Ifølge loven skal overskud eller underskud fordeles i forhold til, hvem der har bidraget til det. Det hedder kontributionsprincippet.<br>Princippet betyder, at vi skal opdele vores kunder i et antal kundegrupper efter Finanstilsynets retningslinjer.<ul><li>Rentegrupper - ud fra grundlagsrente</li><li>Risikogrupper - ud fra forsikringsrisiko</li><li>Omkostningsgrupper - ud fra omkostninger</li></ul></p><p><strong>Grundlagsrente</strong><br>Når vi som forsikringsselskab beregner, hvad vi kan garantere dig i udbetaling, så baserer vi denne beregning på en grundlagsrente. Grundlagsrenten er fastsat ud fra, hvad vi som minimum forventer at opnå i afkast. Disse afkastforventninger varierer gennem tiderne, og derfor kan din pensionsordning være baseret på andre forventninger end andre kunders.</p><p>Vi har opdelt vores kunder i grupper, hvor afkastforventningerne, der ligger til grund for beregningen er nogenlunde ens. Inddelingen er foretaget for at sikre, at du får en rimelig andel af overskuddet.</p><p>Vores pensionsordninger er opdelt i grupper med:</p><ul><li>en gennemsnitlig grundlagsrente op til 0,01 % (rentegruppe B)</li><li>en gennemsnitlig grundlagsrente mellem 0,01 % og 0,51 % (rentegruppe A)</li><li>en gennemsnitlig mellem 0,51 % og 1,51 % (rentegruppe 0)</li><li>en gennemsnitlig grundlagsrente mellem 1,51 % og 2,51 % (rentegruppe 1)</li><li>en gennemsnitlig grundlagsrente mellem 2,51 % og 3,51 % (rentegruppe 2)</li><li>en gennemsnitlig grundlagsrente over 3,51 % (rentegruppe 3)</li></ul><p>Nye pensionsordninger etableres på det højest mulige grundlagsrente, der er tilladt af Finanstilsynet og placeres i rentegruppe B.</p><p>Vi investerer pensionsmidlerne bedst muligt for hver gruppe i forhold til den enkelte gruppes risikovillighed. Se hvordan under "Investeringssammensætning" længere nede på siden.</p><p><strong>Forsikringsrisiko</strong><br>Vi opdeler vores kunder i tre grupper, så du placeres i gruppe med andre kunder, der har en forsikringsrisiko, der ligner din. Som kunde er du solidarisk med de øvrige kunder i din egen gruppe. Det betyder, at prisen afhænger af, hvor mange i din gruppe, der betaler og hvor mange, der skal have udbetalt penge.</p><p>Vi opdeler vores kunder ud fra tre typer forsikringsprodukter:</p><ul><li>Udbetaling ved død</li><li>Udbetaling ved invaliditet</li><li>Udbetaling så længe man lever (Livrente)</li></ul><p>Grupperne er valgt, så f.eks. prisen for en forsikring ved invaliditet ikke berøres af, hvor mange kunder, der dør.</p><p><strong>Særligt for udbetalinger, så længe man lever</strong><br>Vi lever generelt længere end tidligere. Det er derfor blevet dyrere at sikre sig en udbetaling, så længe man lever, end vi oprindeligt havde regnet med. Det skal vores nye kunder, der opretter et livrenteprodukt ikke betale for, så derfor er de placeret i deres egen gruppe.</p><p><strong>Omkostninger</strong><br>De omkostninger, du betaler til Norli, er afstemt i forhold til de udgifter, der er på din ordning. Der er derfor ingen grund til at opdele vores kunder i forskellige omkostningsgrupper.</p>',
      },
      {
        question: 'Hvordan anvender vi overskuddet?',
        answer:
          '<p>Et overskud på pensionsordninger opstår, når f.eks. depotrenten er højere end grundlagsrenten.</p><p>Et sådan overskud kan anvendes til at forøge den garanterede pensionsudbetaling. Det gør vi med de pensionsordninger, der er beregnet på samme grundlag, som det vi beregner vores nye kunders pensionsordninger på.</p><p>F.eks. for kunder med en grundlagsrente på helt op til 4,5 % efter skat er det ikke på samme måde forsvarligt at øge de garanterede pensionsudbetalinger, da det kan være svært for os at opnå den nødvendige forrentning (grundlagsrenten) fremover. Derfor vil det være naturligt at reducere depotrenten for disse kunder, men det, mener vi ikke, er nogen god løsning. Derfor øger vi pensionsopsparingen med depotrenten, men undlader at øge de garanterede pensionsudbetalinger.</p><p>Når din pensionsopsparing er øget tilstrækkeligt i forhold til de garanterede pensionsudbetalinger, vil overskuddet igen blive anvendt til også at hæve de garanterede pensionsudbetalinger.</p>',
      },
      {
        question: 'Hvad betyder det, at du har en garanteret udbetaling?',
        answer:
          '<p><strong>Hvad betyder det, at du har en garanti?</strong><br>Hos os har vi et gennemsnitsrenteprodukt med tilknyttet garanti (ydelsesgaranti). Garantien betyder, at du mindst får den udbetaling, som fremgår af din dækningsoversigt. Derudover kan der være mulighed for at få bonus. Bonus er ikke garanteret.</p><p><strong>Garantiregnskab og bonusregnskab</strong><br>Populært sagt kan man sige, at vi fører to regnskaber for din ordning.</p><p>Et "garantiregnskab" og et "bonusregnskab". Hvis der på pensionstidspunktet står flere penge på bonuskontoen end på garantikontoen, får du bonus. Du får altså det bedste af de to regnskaber.</p><p>Bonus kan opstå ved, at vi i gennemsnit har kunnet forrente din bonuskonto med en højere rente (depotrente), end den rente garantien er baseret på (grundlagsrente). Bonus kan også opstå ved, at vi har trukket et lavere beløb til omkostninger eller forsikringsdækning, end det vi har forudsat ved beregningen af garantien.</p><p>Depotrenten er ikke garanteret og kan godt i perioder være lavere end den rente, garantien er baseret på.</p><p>Se, hvad du er garanteret i pension hos os.<br><a href="/mit-norli" target="_blank" data-gtm-action="link" data-gtm-label="Log på Mit Norli">Log på Mit Norli</a></p>',
      },
      {
        question: 'Hvad er en prognose?',
        answer:
          '<p><strong>Hvad er en prognose?</strong><br>En prognose er ikke noget vi lover, men et udtryk for vores bedste bud på fremtiden. Det er altid de faktiske forhold, der er afgørende for, hvordan din pension udvikler sig.</p><p>Beregning af din forventede pensionsopsparing tager udgangspunkt i Rådet for Pensionsprognosers branche forudsætninger om investeringernes forventede afkast. Derudover indgår vores forventede investeringspolitik og den depotrente, som vi på den baggrund kan give dig. Du skal derfor læse prognosen, som vores kvalificerede bud på, hvordan din pension vil udvikle sig.<br><a href="https://www.afkastforventninger.dk/afkastforventninger/" target="_blank" data-gtm-action="link" data-gtm-label="Se Rådet for Pensionsprognosers branche forudsætninger">Se Rådet for Pensionsprognosers branche forudsætninger</a></p><p>Når du vurderer, om din pension er tilstrækkelig, kan du bruge prognosen som rettesnor, ikke som en garanti.</p><p>Det er nutidsværdien, du skal lægge mest vægt på, når du ser buddet på dine forventede udbetalinger. Nutidsværdien viser dig, hvilken købekraft vi forventer, at din pension har i forhold til i dag. Vi omregner den forventede værdi på pensioneringstidspunktet til nutidskroner på baggrund af branchens forventninger til den fremtidige inflation.</p><p><strong>Usikkerhed ved beregning af din udbetaling</strong><br>Udbetalingen ved forventet afkast er vores bedste bud på, hvor meget du får udbetalt, når du går på pension. Beregningen er lavet ud fra nogle centrale antagelser og gennemsnitsbetragtninger om afkast, inflation, levetid og skat. Vi kan ikke forudsige præcist, hvordan disse forhold udvikler sig i fremtiden. Derfor kan vi heller ikke sige med sikkerhed, hvor meget du vil få udbetalt.</p><p><strong>Usikkerhed om afkast</strong><br>For at give dig en idé om usikkerheden, beregner vi også udbetalingen ved højt afkast og udbetalingen ved lavt afkast. De to tal viser dine udbetalinger, hvis de finansielle markeder udvikler sig bedre eller dårligere end forventet. Din udbetaling vil med stor sandsynlighed (90%) komme til at ligge mellem udbetalingen ved højt afkast og udbetalingen ved lavt afkast.</p><p>Det er kun usikkerheden om udviklingen på de finansielle markeder, der er medregnet i udbetalingen ved højt afkast og udbetalingen ved lavt afkast. Usikkerhed om inflation, skatteregler, levetider mv. er ikke medregnet.</p><p>Læs mere om beregningsmetoden, og hvad prognosen kan bruges til og ikke kan bruges til hos <a href="https://fogp.dk/politik/aabenhed-og-gennemsigtighed/pension/" target="_blank" data-gtm-action="link" data-gtm-label="Forsikring og Pension">Forsikring og Pension</a>.</p>',
      },
      {
        question: 'Hvordan beregner vi ÅOK og ÅOP?',
        answer: `<p>
<strong>Hvordan beregner vi ÅOK og ÅOP?</strong>
<br>
For alle kunder med en traditionel opsparing har vi beregnet og offentliggjort de årlige omkostninger i kr. (ÅOK) og de årlige omkostninger i procent (ÅOP) for året 2010 og frem.
</p>
<p>
ÅOK og ÅOP for året er tilgængelige i Mit Norli umiddelbart efter offentliggørelsen af et årsregnskab. Se hvordan vi ligger i forhold til branchen i øvrigt på 
<a href="https://www.faktaompension.dk" target="_blank" data-gtm-action="link" data-gtm-label="www.faktaompension.dk">www.faktaompension.dk</a>
</p>
<p>
<strong>Omkostningsregnskab</strong>
<br>
Omkostningsregnskabet består af tre dele. En selskabserklæring, en revisionserklæring samt en beskrivelse af den anvendte praksis inkl. et afstemningsskema.
<br>
<a href="/siteassets/site-images/pdf/faq/omkostningsregnskab-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se omkostningsregnskabet">Se omkostningsregnskabet</a>
</p>
<p>
<strong>Forsikring og Pensions krav til metodebeskrivelse</strong>
<br>
Kravene til metodebeskrivelsen er beskrevet i nedenstående LP-information.
<br>
<a href="/siteassets/site-images/pdf/faq/lp-info_2315.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se LP-information">Se LP-information</a>
</p>
<p>
<strong>Arkiv</strong>
<br>
Du kan se dokumenter for tidligere år herunder:
</p>
<p>
<strong>2022</strong>
<br>
<a href="/siteassets/site-images/pdf/faq/omkostningsregnskab-2022.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se omkostningsregnskabet">Se omkostningsregnskabet</a>
<br>
<a href="/siteassets/site-images/pdf/faq/lp-information-3_23.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se LP-information">Se LP-information</a>
<br>
</p>
<p>
<strong>2021</strong>
<br>
<a href="/siteassets/site-images/pdf/faq/metodebeskrivelse-for-aok-og-aop-2021.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se metodebeskrivelsen">Se metodebeskrivelsen</a>
<br>
<a href="/siteassets/site-images/pdf/faq/selskabserklaring-omkring-aok-og-aop-for-2021.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se selskabserklæring" target="_blank">Se selskabserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/revisorerklaring-om-beregning-af-aok-og-aop.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se revisionserklæring">Se revisionserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/lp-information-44_19.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se LP-information">Se LP-information</a>
<br/>
</p>
<p>
<strong>2020</strong>
<br>
<a href="/siteassets/site-images/pdf/faq/metodebeskrivelse-for-aok-og-aop-2020.pdf" target="_blank" data-gtm-action="link" data-gtm-label=">Se metodebeskrivelsen 2020">Se metodebeskrivelsen</a>
<br>
<a href="/siteassets/site-images/pdf/faq/selskabserklaring-omkring-aok-og-aop-for-2020.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se selskabserklæring 2020">Se selskabserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/revisionserklaring-om-beregning-af-aok-og-aop-2020.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se revisionserklæring 2020">Se revisionserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/lp-information-44_19.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se LP-information">Se LP-information</a>
</p>
<p>
<strong>2019</strong>
<br>
<a href="/siteassets/site-images/pdf/faq/metodebeskrivelse-for-aok-og-aop-2019.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se metodebeskrivelsen 2019">Se metodebeskrivelsen</a>
<br>
<a href="/siteassets/site-images/pdf/faq/selskabserklaring-omkring-aok-og-aop-for-2019.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se selskabserklæring 2019">Se selskabserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/revisionserklaring-om-beregning-af-aok-og-aop-2019.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se revisionserklæring 2019">Se revisionserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/lp-information-44_19.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se LP-information 2019">Se LP-information</a>
</p>
<p>
<strong>2018</strong>
<br>
<a href="/siteassets/site-images/pdf/faq/metodebeskrivelse-for-aok-og-aop-2018.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se metodebeskrivelsen 2018">Se metodebeskrivelsen</a>
<br>
<a href="/siteassets/site-images/pdf/faq/selskabserklaring-omkring-aok-og-aop-for-2018.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se selskabserklæring 2018">Se selskabserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/revisionserklaring-om-beregning-af-aok-og-aop-2018.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se revisionserklæring 2018">Se revisionserklæring</a>
<br>
<a href="/siteassets/site-images/pdf/faq/lp-information-50_2018.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Se LP-information 2018">Se LP-information</a>
</p>`,
      },
      {
        question: 'Er der skjulte omkostninger?',
        answer:
          '<p><strong>Er der skjulte omkostninger?</strong><br>Nej, de årlige omkostninger i kroner (ÅOK) og de årlige omkostninger i procent (ÅOP) indeholder alle omkostninger.</p><p><strong>Omkostninger ved gennemsnitsrente</strong><br>Hvis du har en pensionsordning i gennemsnitsrente hos os, kan du se dine omkostninger ved at logge på Mit Norli. Her kan du ved først at vælge "Opsparing m.m."" og derefter "Nøgletal" se dine omkostninger opgjort i kr. (ÅOK) og i % af opsparingen (ÅOP).</p><p><a href="/mit-norli" target="_blank" data-gtm-action="link" data-gtm-label="Log på Mit Norli">Log på Mit Norli</a></p>',
      },
      {
        question: 'Hvordan sammensætter vi investeringerne?',
        answer: `Den faktiske fordeling af investeringerne samt investeringsstrategien for den samlede pensionsopsparing i Norli (tidligere Alm. Brand Liv og Pension) findes i <a href="/siteassets/site-images/pdf/faq/sammensatning/inv-sammensatning-samlet-ult-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="investeringssammensætningen">investeringssammensætningen</a>.</p><p>Hvis du vil se fordelingen på enkelte aktier kan du finde den under "Hvad har vi investeret i?" længere nede på siden.`,
      },
      {
        question: 'Hvordan sammensætter vi investeringerne pr. rentegruppe?',
        answer: `<p>Når vi investerer din pensionsopsparing, gør vi det ud fra en investeringsstrategi. Herunder kan du se investeringsstrategien for i år sammen med en oversigt over, hvordan vi havde investeret ved udgangen af sidste år. Vær opmærksom på, at oversigterne er opdelt efter rentegrupper - læs mere om rentegrupper under "Hvad er kundegrupper?".</p><p><a href="/siteassets/site-images/pdf/faq/sammensatning/inv-sammensatning-rentegruppe-a-ult-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Investeringer - rentegruppe A">Investeringer - rentegruppe A</a><br><a href="/siteassets/site-images/pdf/faq/sammensatning/inv-sammensatning-rentegruppe-b-ult-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Investeringer - rentegruppe B">Investeringer - rentegruppe B</a><br><a href="/siteassets/site-images/pdf/faq/sammensatning/inv-sammensatning-rentegruppe-0-ult-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Investeringer - rentegruppe 0">Investeringer - rentegruppe 0</a><br><a href="/siteassets/site-images/pdf/faq/sammensatning/inv-sammensatning-rentegruppe-1-ult-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Investeringer - rentegruppe 1">Investeringer - rentegruppe 1</a><br><a href="/siteassets/site-images/pdf/faq/sammensatning/inv-sammensatning-rentegruppe-2-ult-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Investeringer - rentegruppe 2">Investeringer - rentegruppe 2</a><br><a href="/siteassets/site-images/pdf/faq/sammensatning/inv-sammensatning-rentegruppe-3-ult-2023.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Investeringer - rentegruppe 3">Investeringer - rentegruppe 3</a></p><p>Du kan i Mit Norli under "Flere oplysninger" se, hvilken rentegruppe din ordning tilhører.<br><a href="/mit-norli" target="_blank" data-gtm-action="link" data-gtm-label="Log på Mit Norli">Log på Mit Norli</a></p>`,
      },
      {
        question: 'Hvad har vi investeret i?',
        answer: `<p>Her finder du den samlede investering, som den så ud ved udgangen af marts 2024.</p><p><a href="/siteassets/site-images/pdf/faq/investeringsaktiver-2024_03_31.pdf" target="_blank" data-gtm-action="link" data-gtm-label="Investeringsaktiver (pdf)">Investeringsaktiver (pdf)</a></p>`,
      },
    ],
  },
]

const annualReports = {
  title: 'Årsrapporter',
  collection: [
    {
      title: 'Årsrapport 2023',
      url: `/siteassets/site-images/pdf/arsrapporter/norli-liv-og-pension-annual-report-2023_final.pdf`,
    },
    {
      title: 'Supplerende korrigerede information 2022',
      url: `/siteassets/site-images/pdf/arsrapporter/supplerende-korrigerede-oplysninger-2022.pdf`,
    },
    {
      title: 'Årsrapport 2022',
      url: `/siteassets/site-images/pdf/arsrapporter/arsrapport-nlp---godkendt-pa-generalforsamling-20230428.pdf`,
    },
    {
      title: 'Årsrapport 2021',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2021.pdf`,
    },
    {
      title: 'Årsrapport 2020',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2020.pdf`,
    },
    {
      title: 'Årsrapport 2019',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2019.pdf`,
    },
    {
      title: 'Årsrapport 2018',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2018.pdf`,
    },
    {
      title: 'Årsrapport 2017',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2017.pdf`,
    },
    {
      title: 'Årsrapport 2016',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2016.pdf`,
    },
    {
      title: 'Årsrapport 2015',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2015.pdf`,
    },
    {
      title: 'Årsrapport 2014',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2014.pdf`,
    },
    {
      title: 'Årsrapport 2013',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2013.pdf`,
    },
    {
      title: 'Årsrapport 2012',
      url: `/siteassets/site-images/pdf/arsrapporter/aarsrapport_liv-og-pension_2012.pdf`,
    },
  ],
}

const remuneration = {
  title: 'Vederlagsrapporter',
  collection: [
    {
      title: 'Vederlagsrapport 2023',
      url: `/siteassets/site-images/pdf/vederlagsrapporter/nl-vederlagsrapport-2023.pdf`,
    },
    {
      title: 'Vederlagsrapport 2022',
      url: `/siteassets/site-images/pdf/vederlagsrapporter/vederlagsrapport-2022-1.pdf`,
    },
   
  ],
}

const financeReports = {
  title: 'Finanstilsynet',
  collection: [
    {
      title: 'Redegørelse for funktionsinspektion (1. kvartal 2024)',
      url: `/siteassets/site-images/pdf/om-norli/finanstilsynet/redegorelse-om-funktionsinspektion-i-norli-liv-og-pension-livsforsikring-as.pdf `,
    },
    {
      title: 'Kommentarer til redegørelse (1. kvartal 2024)',
      url: `/siteassets/site-images/pdf/om-norli/finanstilsynet/nl-kommentarer-til-finanstilsynet-redegorelse.pdf`,
    },
    {
      title: 'Redegørelse om påbud (1. kvartal 2020)',
      url: `/siteassets/site-images/pdf/om-norli/finanstilsynet/1-kvartal-2020_kommentar-til-pabud-om-vardiansattelse-af-hensattelser.pdf`,
    },
    {
      title: 'Kommentarer til redegørelse (1. kvartal 2020)',
      url: `/siteassets/site-images/pdf/om-norli/finanstilsynet/1-kvartal-2020_redegoerelse-om-paabud-til-forsikringsselskabet-alm.-brand-liv-og-pension.pdf`,
    },
    {
      title: 'Tilbagemelding på redegørelse (1. kvartal 2020)',
      url: `/siteassets/site-images/pdf/om-norli/finanstilsynet/1-kvartal-2020_tilbagemelding-paa-finanstilsynets-paabud-om-vardiansattelse.pdf`,
    },
   
  ],
}

const riskReports = {
  title: 'Risikorapportering',
  collection: [
    {
      title: 'Risikorapport 2023',
      url: `/siteassets/site-images/pdf/risikorapportering/sfcr-2023-norli-liv-og-pension-20240402.pdf`,
    },
    {
      title: 'Risikorapport 2022',
      url: `/siteassets/site-images/pdf/risikorapportering/sfcr-2022-norli-liv-og-pension-20230329.pdf`,
    },
    {
      title: 'Risikorapport 2021',
      url: `/siteassets/site-images/pdf/risikorapportering/2021-risikorapport-alm-brand-group.pdf`,
    },
    {
      title: 'Risikorapport 2020',
      url: `/siteassets/site-images/pdf/risikorapportering/2020-risikorapport-alm-brand-liv-og-pension.pdf`,
    },
    {
      title: 'Risikorapport 2019',
      url: `/siteassets/site-images/pdf/risikorapportering/2019-risikorapport-alm-brand-liv-og-pension.pdf`,
    },
    {
      title: 'Risikorapport 2018',
      url: `/siteassets/site-images/pdf/risikorapportering/2018-risikorapport-alm-brand-liv-og-pension.pdf`,
    },
    {
      title: 'Risikorapport 2017',
      url: `/siteassets/site-images/pdf/risikorapportering/2017-risikorapport-alm-brand-liv-og-pension.pdf`,
    },
    {
      title: 'Risikorapport 2016',
      url: `/siteassets/site-images/pdf/risikorapportering/2016-risikorapport-alm-brand-liv-og-pension.pdf`,
    },
  ],
}

const politicalReports = {
  title: 'Politikker mv.',
  collection: [
    // {
    //   title: 'Vederlagspolitik',
    //   url: `/siteassets/site-images/pdf/politikker/politik-for-vederlag_april2022.pdf`,
    // },
    {
      title: 'Politik for ansvarlige investeringer',
      url: `/siteassets/site-images/pdf/politikker/politik-for-ansvarlige-investeringer-og-aktivt-ejerskab---norli-gruppen-2023-20230328-ekskl.-rev.-log.pdf`,
    },
  ],
}

const nordicRiskReports = {
  title: 'Risikorapportering',
  collection: [
    {
      title: 'Risikorapport 2023',
      url: `/siteassets/site-images/pdf/risikorapportering/nh-sfcr-2023-norli-group-v.2.pdf`,
    },
    {
      title: 'Risikorapport 2022',
      url: `/siteassets/site-images/pdf/risikorapportering/sfcr-2022-nordic-ip-20230329.pdf`,
    },
   ],
}

const nordicFinanceReports = {
  title: 'Finanstilsynet',
  collection: [
    {
      title: 'Redegørelse for funktionsinspektion (1. kvartal 2024)',
      url: `/siteassets/site-images/pdf/om-nh/finanstilsynet/redegorelse-om-funktionsinspektion-i-nordic-ip-dk-aps.pdf`,
    },
    {
      title: 'Kommentarer til redegørelse (1. kvartal 2024)',
      url: `/siteassets/site-images/pdf/om-nh/finanstilsynet/nh-kommentarer-til-finanstilsynet-redegorelse.pdf`,
    }
  ],
}

const nordicAnnualReports = {
  title: 'Årsrapporter',
  collection: [
    {
      title: 'Årsrapport 2023',
      url: `/siteassets/site-images/pdf/arsrapporter/nordic-ip-dk-aps-2023-annual-report_final.pdf`,
    },
    {
      title: 'Supplerende korrigerende oplysninger 2022',
      url: `/siteassets/site-images/pdf/arsrapporter/supplerende-korrigerende-informationer-2022.pdf`,
    },
    {
      title: 'Årsrapport 2022',
      url: `/siteassets/site-images/pdf/arsrapporter/arsrapport-2022-nordic-ip-dk-aps---underskrevet.pdf`,
    },
  ],
}

const remunerationAnnualReports = {
  title: 'Vederlagsrapporter',
  collection: [
    {
      title: 'Vederlagsrapport 2023',
      url: `/siteassets/site-images/pdf/vederlagsrapporter/nh-vederlagsrapport-2023_1.pdf`,
    },
    {
      title: 'Vederlagsrapport 2022',
      url: `/siteassets/site-images/pdf/vederlagsrapporter/nh-vederlagsrapport-2022.pdf`,
    },
  ],
}

const sustainabilityFactorsReports = {
  title: 'Bæredygtighedsfaktorer',
  collection: [
    {
      title: 'Manglende hensyntagen til investeringsbeslutningers negative indvirkninger på bæredygtighedsfaktorer',
      url: `/siteassets/site-images/pdf/baredygtighedsfaktorer/baeredygtighedsfaktorer---2023.pdf`,
    },
  ],
}

const historicTerms = {
  title: 'Historiske betingelser',
  collection: [
  {
      title: 'Norli Betingelser 5258 Forsikring ved Nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/nedsat-erhvervsevne-betingelser_5258.pdf`,
    },
    {
      title: 'Norli Betingelser 5515 Firmakoncept-forsikring ved nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/nedsat-erhvervsevne-firmakoncept-betingelser_5515.pdf`
    },
    {
      title: 'Alm Brand Betingelser 5150 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5150-livsforsikring-02.1986.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5151 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5151-livsforsikring-1989.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5152 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5152-livsforsikring-11.1994.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5160 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5160-livsforsikring-08.1994.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5161 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5161-livsforsikring.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5162 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5162-livsforsikring-10.1999.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5163 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5163-livsforsikring.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5164 Forsikringsklasse VI',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5164-forsikringsklasse-vi.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5165 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5165-livsforsikring_vs2.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5166 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5166-livsforsikring_vs2.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5167 Livsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5167-livsforsikring_vs2.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5169 Livs- og pensionsforsikring Juni 2010',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5169-livs-og-pensionsforsikring-06.2010_vs2.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5169 Livs- og pensionsforsikring Aug 2010',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5169-livs-og-pensionsforsikring-08.2010.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5200 Forsikringsklasse VI',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5200-forsikringsklasse-vi-07.2004.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5250 Personforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5250-personforsikring-04.1996.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5251 Personforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5251-personforsikring-10.1997.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5252 Tab af arbejdsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5252-tab-af-arbejdsevne-11.1999.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5253 Nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5253-nedsat-erhvervsevne.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5254 Nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5254-nedsat-erhvervsevne.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5255 Nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5255-nedsat-erhvervsevne.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5256 Nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5256-nedsat-erhvervsevne.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5257 Forsikring ved Nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5257-forsikring-ved-nedsat-erhvervsevne.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5272 Plusliv',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5272-plusliv-07.2004.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5273 Plusliv',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5273-plusliv.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5274 PlusLIV',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5274-plusliv.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5275 plusLIV ved dødsfald',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5275-plusliv-ved-dodsfald.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5400 Forsikringsklasse VI - kapitaliseringsvirksomhed',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5400-forsikringsklasse-vi---kapitaliseringsvirksomhed.pdf`,
    },
    {
      title: 'Alm Brand Betingelser 5514 Firmakoncept - forsikring ved nedsat erhvervsevne',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/alm-brand-betingelser-5514-firmakoncept---forsikring-ved-nedsat-erhvervsevne.pdf`,
    },
    {
      title: 'Forenede Gruppeliv 20040101 Gruppelivsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-20040101-gruppelivsforsikring.pdf`,
    },
    {
      title: 'Forenede Gruppeliv 20080101 Gruppelivsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-20080101-gruppelivsforsikring.pdf`,
    },
    {
      title: 'Forenede Gruppeliv 20090101 Gruppelivsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-20090101-dakning-ved-kritisk-sygdom.pdf`,
    },
    {
      title: 'Forenede Gruppeliv 20120101 Dækning ved kritisk sygdom',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-20120101-dakning-ved-kritisk-sygdom.pdf`,
    },
    {
      title: 'Forenede Gruppeliv 20120101 Gruppelivsforsikring',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-20120101-gruppelivsforsikring.pdf`,
    },
    {
      title: 'Forenede Gruppeliv 20140101 Dækning ved kritisk sygdom',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-20140101-dakning-ved-kritisk-sygdom.pdf`,
    },
    {
      title: 'Forenede Gruppeliv Betingelser Gruppelivsforsikring Jan 2021',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-betingelser-gruppelivsforsikring-01.2021.pdf     `,
    },
    {
      title: 'Forenede Gruppeliv Betingelser Kritisk sygdom 2020',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/forenede-gruppeliv-betingelser-kritisk-sygdom-2020.pdf`,
    },
    {
      title: 'Gruppeliv 20210101 - død og invaliditet',
      url: '/siteassets/site-images/pdf/forsikringstyper/livsforsikring/gruppeliv-betingelser---dod-og-invaliditet-20210101.pdf',
    },
    {
      title: 'Gruppeliv 20210101 - kritisk sygdom',
      url: '/siteassets/site-images/pdf/forsikringstyper/livsforsikring/gruppeliv-betingelser---kritisk-sygdom-20210101.pdf',
    },
    {
      title: 'Gruppeliv 20210101 - kritisk sygdom (børn)',
      url: '/siteassets/site-images/pdf/forsikringstyper/livsforsikring/gruppeliv---kritisk-sygdom-born-20210101.pdf',
    },
    {
      title: 'Nordlyset Betingelser 1462 Livsforsikring Juli 1982',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/nordlyset-betingelser-1462-livsforsikring-07.1982.pdf`,
    },
    {
      title: 'Utrecht A 115 - nr. 129 af 15 april 1930.pdf',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/utrecht-a-115---nr.-129-af-15-april-1930.pdf`,
    },
    {
      title: 'Utrecht Betingelser Livsforsikring 002.11.92',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/utrecht-betingelser-livsforsikring-002.11.92.pdf`,
    },
    {
      title: 'Utrecht Betingelser Livsforsikring 004.02.94',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/utrecht-betingelser-livsforsikring-004.02.94.pdf`,
    },
    {
      title: 'Utrecht Betingelser Livsforsikring 005.07.94',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/utrecht-betingelser-livsforsikring-005.07.94.pdf`,
    },
    {
      title: 'Utrecht Betingelser Livsforsikring 10-82',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/utrecht-betingelser-livsforsikring-10-82.pdf`,
    },
    {
      title: 'Utrecht Betingelser Livsforsikring BBp 500-10-63',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/utrecht-betingelser-livsforsikring-bbp-500-10-63.pdf`,
    },
    {
      title: 'Zurich Betingelser 55-0 af 1. oktober 1982 21.0034 pm (05.1999)',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/zurich-betingelser-55-0-af-1.-oktober-1982-21.0034pm05.1999.pdf`,
    },
    {
      title: 'Zurich Betingelser 65-0 af 1. juli 1999 21.0061-2.pm',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/zurich-betingelser-55-0-af-1.-oktober-1982-21.0034pm05.1999.pdf`,
    },
    {
      title: 'Zurich Betingelser af 1. januar 1996 Invaliderente 21.0044 pm (08.06.1999)',
      url: `/siteassets/site-images/pdf/priser-vilkar-og-dokumenter/historiske-betingelser/zurich-betingelser-55-0-af-1.-oktober-1982-21.0034pm05.1999.pdf`,
    },
  ],
}

const tabsData = [
  {
    title: 'Alders&shy;pension',
    content:
      'Få hele din opsparing udbetalt, når du går på pension. Du kan indbetale 9.100 kr. om året (2024-sats), indtil 7 år før din folkepension. Herefter kan du indbetale 58.900 kr. om året (2024-sats). Udbetalingen er skattefri og modregnes ikke i folkepensionen.',
  },
  {
    title: 'Rate&shy;pension',
    content:
      'Få en udbetaling hver måned i en periode på mellem 10 og 30 år. Du kan indbetale op til 63.100 kr. om året (2024-sats). Udbetalingerne beskattes som almindelig indkomst. Opsparingen udbetales til dine efterladte, hvis du dør.',
  },
  {
    title: 'Livsvaring pension',
    content:
      'Giver dig en udbetaling hver måned, så længe du lever. Der er ingen øvre grænse for, hvor meget du kan indbetale. Udbetalingerne beskattes som almindelig indkomst. Hvis du dør, kan dine efterladte modtage udbetalingerne i en periode under visse forudsætninger.',
  },
]

const scrollTo = (id: string): void => {
  const el = document.getElementById(id)
  if (!el) return
  const headerOffset = 100
  const elPos = el.getBoundingClientRect().top
  const offsetPosition = elPos + window.pageYOffset - headerOffset

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })

  if (store.state.isMenuOpen) store.commit('toggleMenu', false)
}

const isMobileHeader = computed(() => {
  return store.state.windowWidth < 920
})
const isMenuActive = computed(() => {
  return store.state.isMenuOpen
})
const isModalActive = computed(() => {
  return store.state.isModalOpen
})

const handleOverlayClick = () => {
  if (store.state.isMenuOpen) store.commit('toggleMenu', false)
  if (store.state.isModalOpen) store.commit('toggleModal', false)
}

const isCalculatorOpen = computed(() => {
  return store.state.isCalculatorOpen
})

const toggleCalculator = () => {
  store.commit('toggleCalculator', !store.state.isCalculatorOpen)
}

let isVideoPlaying = false;

const toggleVideo = () => {
  const video = document.getElementById('bodyVideo') as HTMLVideoElement;

  if (isVideoPlaying) {
    video.pause();
  } else {
    video.play();
  }

  isVideoPlaying = !isVideoPlaying;
}

const showPensionBranch = ref(false);

const togglePensionBranch = () => {
  showPensionBranch.value = !showPensionBranch.value;
}

const updatePensionsHeight = () => {
  // Update nav height when opening/closing accordion
  const pensionNav = nav.value.find(item => item.href === 'pensions');
  
  if (pensionNav) {
    const accordionElement = document.getElementById('pensionsAccordion');
    const accordionHeight = accordionElement ? accordionElement.offsetHeight : 0;
    pensionNav.positionBottom = showPensionBranch.value ? pensionNav.positionBottom + accordionHeight : pensionNav.positionBottom - accordionHeight;
  }
}

onMounted(() => {
  const windowHeight = window.innerHeight;
  const windowWidth = window.outerWidth;

  scrollPosition.value = windowHeight;

  document.addEventListener('scroll', () => {
    scrollPosition.value = (document.documentElement.scrollTop || document.body.scrollTop) + windowHeight;
  });

  setTimeout(() => {
    nav.value.forEach((item) => {
      item.positionTop = document.getElementById(item.href)!.getBoundingClientRect().top
      item.positionBottom = document.getElementById(item.href)!.getBoundingClientRect().bottom

      if (windowWidth >= 1366) {
        if (item.offsetTop > 0) {
          item.positionTop = item.positionTop + item.offsetTop;
        }
        if (item.offsetBottom > 0) {
          item.positionBottom = item.positionBottom + item.offsetBottom;
        }
      }
    })
  }, 250);

  setTimeout(() => {
    if (route.hash) scrollTo(route.hash.slice(1))
  }, 350);
});
</script>

<style lang="scss">
:root {
  --headerHeight: 120px;
  --headerHeightMobile: 70px;
}

.bg {
  &-primary {
    background-color: $color-primary;
    color: $color-white;
  }

  &-secondary {
    background-color: $color-secondary;
    color: $color-white;
  }

  &-tertiary {
    background-color: $color-tertiary;
  }

  &-white {
    background-color: $color-white;
    color: $color-black;
  }
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--headerHeight);
  border-bottom: 1px solid grey;
  background-color: white;
  z-index: 9;

  &--mobile {
    height: var(--headerHeightMobile);
  }

  nav {
    flex: 1;

    ul.menu {
      @include remove-list-style();

      li a {
        font-size: 18px;
        text-decoration: none;
        letter-spacing: 0.02em;

        &.active {
          font-weight: 700;
        }
      }

      &--mobile {
        position: fixed;
        right: 0;
        top: var(--headerHeightMobile);
        z-index: 9;
        flex-direction: column;
        background-color: $color-white;
        border: 1px solid grey;
        padding: 20px !important;
        transform: translateX(100%);

        li a {
          font-size: $font-size-md;
          padding: 10px 0;
          display: block;
        }

        &.active {
          transform: translateX(0);
        }
      }
    }
  }
}

.overlay {
  background-color: rgba($color-black, 0.3);
  position: fixed;
  z-index: z(overlay, header);
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  cursor: pointer;
}

.button {
  text-decoration: none;
  padding: 8px 20px;
  color: currentColor !important;
  font-size: 18px;
  background: none;
  border: 2px solid currentColor;
  display: inline-flex;
  align-self: flex-start;
  cursor: pointer;

  &--cta {
    background-color: $color-primary;
    border-color: $color-primary;
    color: $color-white !important;
  }

  &--solid-white {
    background-color: $color-white;
    color: $color-black !important;
    border-color: $color-white;
  }
}

.hero {
  &__left {
    background-position: center;
    background-size: cover;
    min-height: 300px;
    position: relative;

    &::after {
      content: '';
      width: 100%;
      height: 100%;
      background: rgb(0, 0, 0);
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 70%);
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  &__text {
    margin-top: auto;
    position: relative;
    z-index: 1;

    p {
      font-size: 18px;

      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  &__title {
    position: relative;
    z-index: 1;
  }

  &__splash {
    width: 150px;
    height: 150px;
    margin-left: auto;
    border-radius: 50%;
    text-align: center;
    position: relative;
    bottom: -10px;
    z-index: 9;

    span {
      line-height: 1.2;
    }
  }
}

.banner {
  &__desktop {
    width: 100%;
    height: auto;

    @media screen and (max-width: $breakpoint-xs) {
      display: none;
    }
  }

  &__mobile {
    display: none;

    @media screen and (max-width: $breakpoint-xs) {
      display: block;
      width: 100%;
    }
  }
}

.card {
  &__icon {
    color: $color-primary;

    svg circle {
      fill: $color-primary;
    }
  }
}

.modal-trigger {
  -webkit-appearance: none;
  border: 0;
  background: 0;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
  color: $color-black;
}

.video-modal {
  display: flex;
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 50%;
  aspect-ratio: 16/9;
  z-index: 1;
  background-color: $color-secondary;
  border-radius: 4px;  

  @media (max-width: 919px) {
    width: 95%;
  }

  .close-overlay {
    position: absolute;
    top: 15px;
    right: 15px;
    background-color: $color-white;
    border-radius: 50%;
    border: 0;
    width: 20px;
    height: 20px;
    font-weight: $font-weight-bold;
    font-size: $font-size-xxs;
    color: $color-secondary;
    cursor: pointer;
    z-index: 1;

    &:hover {
      background-color: $color-primary;
      color: $color-white;
    }
  }

  video {
    width: 100%;
  }
}

.body-video {
  cursor: pointer;
}

.pension-read-more {
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  color: $color-white;
  text-decoration: underline;
  position: relative;

  &::after {
      $chevronIcon: &;
      content: '';
      -webkit-mask: url(/src/assets/svg/chevron.svg) center center no-repeat;
      background-color: $color-white;
      height: $spacer-lg;
      width: $spacer-lg;
      margin-left: $spacer-sm;
      transform: rotate(180deg);
      transition: transform 0.3s ease;
      will-change: rotate;
      flex-shrink: 0;
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
      display: block;
      position: absolute;
      right: -$spacer-lg;
      top: 10px;
  }

  &.active::after {
    transform: rotate(0);
  }

  &:hover {
    text-decoration: none;
  }
}

footer {
  .footer-logo {
    margin: 20px 0 11px;
  }

  svg {
    path,
    polygon,
    rect {
      fill: $color-white !important;
    }
  }
}
</style>


